// src/hooks/useLocalisation.ts
import { useMemo } from 'react';
import { useLocale } from '../contexts/LocaleContext';
import enZA from '../locales/en-ZA.json';
import nlNL from '../locales/nl-NL.json';

// Helper function to get nested values from object using dot notation
const getNestedValue = (obj: any, path: string): string => {
  return path.split('.').reduce((current, key) => current?.[key], obj) || path;
};

// Helper function to replace placeholders in strings
const interpolate = (str: string, params?: Record<string, string>): string => {
  if (!params) return str;

  return Object.entries(params).reduce((result, [key, value]) => {
    return result.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
  }, str);
};

export const useLocalisation = () => {
  // Get locale from context instead of parameter
  const { locale } = useLocale();

  const strings = useMemo(() => {
    switch (locale) {
      case 'nl-NL':
        return nlNL;
      case 'en-ZA':
      default:
        return enZA;
    }
  }, [locale]);

  // Main translation function with fallback to en-ZA
  const t = (key: string, params?: Record<string, string>): string => {
    let value = getNestedValue(strings, key);

    // Fallback to en-ZA if key doesn't exist in current locale
    if (value === key && locale !== 'en-ZA') {
      value = getNestedValue(enZA, key);
    }

    return interpolate(value, params);
  };

  // Navigation-specific helper - TypeScript will auto-infer the correct keys from the JSON
  const nav = (key: keyof typeof enZA.navigation): string => {
    return strings.navigation[key] || enZA.navigation[key];
  };

  // Homepage-specific helper
  const home = {
    overview: {
      title: strings.homepage?.overview?.title || enZA.homepage.overview.title,
      stat: (key: keyof typeof enZA.homepage.overview.stats) => {
        return (
          strings.homepage?.overview?.stats[key] ||
          enZA.homepage.overview.stats[key]
        );
      },
    },
  };

  // ARIA labels helper - TypeScript will auto-infer the correct keys from the JSON
  const aria = (
    key: keyof typeof enZA.aria,
    params?: Record<string, string>
  ): string => {
    const value = strings.aria?.[key] || enZA.aria[key];
    return interpolate(value, params);
  };

  return {
    t, // Generic translation function
    nav, // Navigation strings
    home, // Homepage strings
    aria, // ARIA labels
    locale, // Current locale
    strings, // Raw strings object for advanced usage
  };
};

export default useLocalisation;
